import { AI_SERVICE_URL } from '@/config/constants';

export const AI_STORIES_EVENT_TYPES = {
    CONNECTION: 'connection',
    DELTA: 'delta',
    PAYLOAD: 'payload',
    COMPLETE: 'complete',
    ERROR: 'error',
};

const AI_STORIES_TIMEOUT_MS = 90000;

/**
 * @typedef {Object} Insight
 * @property {string} id
 * @property {string} label
 * @property {string} value
 * @property {string} metric
 * @property {string} cta_text
 * @property {string} prompt
 */

/**
 * Stream the entity-page insight payload (`{insights}`).
 *
 * The backend emits exactly one `payload` event when the LLM call completes
 * (or replays it instantly on a cache hit), so handlers see one onPayload
 * callback followed by onComplete.
 *
 * @param {Object} params
 * @param {string} params.entityId
 * @param {'brand' | 'category'} params.entityType
 * @param {string} [params.country]
 * @param {string} [params.dataSource]
 * @param {string} [params.week]
 * @param {string} [params.entityRoute]
 * @param {Object} handlers
 * @param {Function} [handlers.onConnection]
 * @param {Function} [handlers.onDelta]     - { text } — JSON-text fragment from the model. Accumulate + partial-parse to render cards progressively.
 * @param {Function} [handlers.onPayload]   - { insights } — final canonical list; use as source-of-truth if partial parsing missed anything.
 * @param {Function} [handlers.onComplete]
 * @param {Function} [handlers.onError]
 *
 * @returns {{ abort: () => void }}
 */
export const streamAiStory = (
    { entityId, entityType, country, dataSource, week, entityRoute },
    handlers = {},
) => {
    const abortController = new AbortController();
    const apiUrl = `${AI_SERVICE_URL}/ai/stories/`;

    const timeoutId = setTimeout(() => {
        abortController.abort();
        handlers.onError?.(new Error('AI story generation timed out'));
    }, AI_STORIES_TIMEOUT_MS);

    const wrappedHandlers = {
        ...handlers,
        onComplete: (data) => {
            clearTimeout(timeoutId);
            handlers.onComplete?.(data);
        },
        onError: (error) => {
            clearTimeout(timeoutId);
            handlers.onError?.(error);
        },
    };

    const startStream = async () => {
        try {
            const body = {
                entity_id: entityId,
                entity_type: entityType,
            };
            if (country) body.country = country;
            if (dataSource) body.data_source = dataSource;
            if (week) body.week = week;
            if (entityRoute) body.entity_route = entityRoute;

            // The fetch interceptor at
            // `utils/api/registerAuthenticatedFetchInterceptor.js` attaches
            // the Auth0 bearer token and Content-Type automatically for the
            // AI_SERVICE_URL host. No manual headers needed here.
            const response = await fetch(apiUrl, {
                method: 'POST',
                body: JSON.stringify(body),
                signal: abortController.signal,
            });

            if (!response.ok) {
                throw new Error(
                    `HTTP ${response.status}: ${response.statusText}`,
                );
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';

            const handleEvent = (eventType, data) => {
                switch (eventType) {
                    case AI_STORIES_EVENT_TYPES.CONNECTION:
                        wrappedHandlers.onConnection?.(data);
                        break;
                    case AI_STORIES_EVENT_TYPES.DELTA:
                        wrappedHandlers.onDelta?.(data);
                        break;
                    case AI_STORIES_EVENT_TYPES.PAYLOAD:
                        wrappedHandlers.onPayload?.(data);
                        break;
                    case AI_STORIES_EVENT_TYPES.COMPLETE:
                        wrappedHandlers.onComplete?.(data);
                        break;
                    case AI_STORIES_EVENT_TYPES.ERROR:
                        wrappedHandlers.onError?.(
                            new Error(data.error || 'AI story error'),
                        );
                        break;
                    default:
                        console.warn(
                            'Unknown AI Stories SSE event type:',
                            eventType,
                        );
                }
            };

            const parseSSE = (text) => {
                const lines = text.split('\n');
                let event = null;
                let data = null;

                for (const line of lines) {
                    if (line.startsWith('event: ')) {
                        event = line.slice(7);
                    } else if (line.startsWith('data: ')) {
                        data = line.slice(6);
                    } else if (line === '' && event && data) {
                        try {
                            handleEvent(event, JSON.parse(data));
                        } catch (e) {
                            console.error('Failed to parse SSE data:', e);
                        }
                        event = null;
                        data = null;
                    }
                }

                const lastEmptyIndex = text.lastIndexOf('\n\n');
                return lastEmptyIndex >= 0
                    ? text.slice(lastEmptyIndex + 2)
                    : text;
            };

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });
                buffer = parseSSE(buffer);
            }
            if (buffer) parseSSE(buffer + '\n\n');
        } catch (error) {
            if (error.name === 'AbortError') return;
            console.error('AI story stream error:', error);
            wrappedHandlers.onError?.(error);
        }
    };

    startStream();

    return {
        abort: () => {
            clearTimeout(timeoutId);
            abortController.abort();
        },
    };
};
